import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useToast } from '@/hooks/use-toast';
import { 
  Phone, Mail, Bot, FileSearch, AlertTriangle, CheckCircle2, 
  XCircle, RefreshCw, Eye, EyeOff, Copy, ExternalLink, Settings2,
  MessageSquare, Sparkles, Cloud, UserSearch
} from 'lucide-react';
import { cn } from '@/lib/utils';

interface Integration {
  id: string;
  name: string;
  description: string;
  category: 'communication' | 'ai' | 'document' | 'monitoring' | 'enrichment';
  icon: React.ComponentType<{ className?: string }>;
  secretKeys: string[];
  webhookUrl?: string;
  docsUrl?: string;
  features: string[];
}

const integrations: Integration[] = [
  // Communication
  {
    id: 'twilio',
    name: 'Twilio',
    description: 'Voice calls, SMS, and WhatsApp messaging',
    category: 'communication',
    icon: Phone,
    secretKeys: ['TWILIO_ACCOUNT_SID', 'TWILIO_AUTH_TOKEN', 'TWILIO_API_KEY', 'TWILIO_API_SECRET', 'TWILIO_TWIML_APP_SID', 'TWILIO_PHONE_NUMBER'],
    webhookUrl: '/functions/v1/twilio-webhook',
    docsUrl: 'https://www.twilio.com/docs',
    features: ['Outbound calls', 'Inbound calls', 'SMS messaging', 'WhatsApp messaging', 'Call recording'],
  },
  {
    id: 'resend',
    name: 'Resend',
    description: 'Transactional email delivery',
    category: 'communication',
    icon: Mail,
    secretKeys: ['RESEND_API_KEY'],
    webhookUrl: '/functions/v1/resend-webhook',
    docsUrl: 'https://resend.com/docs',
    features: ['Send emails', 'Email templates', 'Delivery tracking', 'Inbound email parsing'],
  },
  // AI Services
  {
    id: 'openai',
    name: 'OpenAI',
    description: 'GPT models for AI-powered features',
    category: 'ai',
    icon: Sparkles,
    secretKeys: ['OPENAI_API_KEY'],
    docsUrl: 'https://platform.openai.com/docs',
    features: ['Resume parsing', 'JD generation', 'Candidate insights', 'Communication drafts'],
  },
  {
    id: 'lovable-ai',
    name: 'Lovable AI',
    description: 'Google Gemini models via Lovable Cloud',
    category: 'ai',
    icon: Cloud,
    secretKeys: ['LOVABLE_API_KEY'],
    docsUrl: 'https://docs.lovable.dev',
    features: ['Resume deep parsing', 'Skills extraction', 'AI summaries', 'Candidate scoring'],
  },
  // Document Processing
  {
    id: 'google-docai',
    name: 'Google Document AI',
    description: 'OCR and document text extraction',
    category: 'document',
    icon: FileSearch,
    secretKeys: ['GOOGLE_SERVICE_ACCOUNT_KEY', 'GOOGLE_CLOUD_PROJECT_ID', 'GOOGLE_DOCAI_PROCESSOR_ID', 'GOOGLE_CLOUD_LOCATION'],
    docsUrl: 'https://cloud.google.com/document-ai/docs',
    features: ['PDF text extraction', 'Image OCR', 'Resume parsing', 'Document classification'],
  },
  // Monitoring
  {
    id: 'sentry',
    name: 'Sentry',
    description: 'Error tracking and performance monitoring',
    category: 'monitoring',
    icon: AlertTriangle,
    secretKeys: ['VITE_SENTRY_DSN', 'SENTRY_AUTH_TOKEN'],
    docsUrl: 'https://docs.sentry.io',
    features: ['Error tracking', 'Performance monitoring', 'Release tracking', 'User feedback'],
  },
  // Enrichment
  {
    id: 'lusha',
    name: 'Lusha',
    description: 'Contact discovery and enrichment',
    category: 'enrichment',
    icon: UserSearch,
    secretKeys: ['LUSHA_API_KEY'],
    docsUrl: 'https://www.lusha.com/docs/',
    features: ['Email discovery', 'Phone discovery', 'Contact enrichment', 'Bulk discovery', 'Credit tracking'],
  },
];

const categoryLabels = {
  communication: 'Communication',
  ai: 'AI Services',
  document: 'Document Processing',
  monitoring: 'Monitoring',
  enrichment: 'Enrichment',
};

const categoryIcons = {
  communication: MessageSquare,
  ai: Bot,
  document: FileSearch,
  monitoring: AlertTriangle,
  enrichment: UserSearch,
};

export default function IntegrationsSettings() {
  const { toast } = useToast();
  const [testingId, setTestingId] = useState<string | null>(null);
  const [showSecrets, setShowSecrets] = useState<Record<string, boolean>>({});

  // Fetch configured secrets from app_settings (we store integration status there)
  const { data: integrationStatus, isLoading, refetch } = useQuery({
    queryKey: ['integration-status'],
    queryFn: async () => {
      // Check which integrations have their secrets configured
      // We'll check the edge function health or stored status
      const { data: settings } = await supabase
        .from('app_settings')
        .select('*')
        .like('setting_key', 'integration_%');
      
      return settings || [];
    },
  });

  // Fetch recent errors related to integrations
  const { data: recentErrors } = useQuery({
    queryKey: ['integration-errors'],
    queryFn: async () => {
      const { data } = await supabase
        .from('error_logs')
        .select('*')
        .or('error_code.ilike.%twilio%,error_code.ilike.%resend%,error_code.ilike.%openai%,error_code.ilike.%google%,error_code.ilike.%sentry%,error_code.ilike.%lusha%')
        .order('created_at', { ascending: false })
        .limit(10);
      
      return data || [];
    },
  });

  const getIntegrationStatus = (integration: Integration): 'connected' | 'partial' | 'not_configured' => {
    // Check if we have a stored status
    const storedStatus = integrationStatus?.find(
      s => s.setting_key === `integration_${integration.id}_status`
    );
    if (storedStatus?.setting_value === 'connected') return 'connected';
    if (storedStatus?.setting_value === 'partial') return 'partial';
    
    // Default based on known secrets from the project
    const knownSecrets = [
      'TWILIO_ACCOUNT_SID', 'TWILIO_AUTH_TOKEN', 'TWILIO_API_KEY', 'TWILIO_API_SECRET',
      'TWILIO_TWIML_APP_SID', 'TWILIO_PHONE_NUMBER', 'RESEND_API_KEY', 'OPENAI_API_KEY',
      'LOVABLE_API_KEY', 'GOOGLE_SERVICE_ACCOUNT_KEY', 'GOOGLE_CLOUD_PROJECT_ID',
      'GOOGLE_DOCAI_PROCESSOR_ID', 'GOOGLE_CLOUD_LOCATION', 'VITE_SENTRY_DSN', 'SENTRY_AUTH_TOKEN',
      'LUSHA_API_KEY'
    ];
    
    const configuredCount = integration.secretKeys.filter(k => knownSecrets.includes(k)).length;
    if (configuredCount === integration.secretKeys.length) return 'connected';
    if (configuredCount > 0) return 'partial';
    return 'not_configured';
  };

  const testIntegration = async (integration: Integration) => {
    setTestingId(integration.id);
    try {
      let success = false;
      let message = '';

      switch (integration.id) {
        case 'twilio':
          // Test Twilio by calling the token endpoint
          const { error: twilioError } = await supabase.functions.invoke('twilio-token');
          success = !twilioError;
          message = twilioError ? twilioError.message : 'Twilio connection successful';
          break;
        
        case 'resend':
          // We can't easily test Resend without sending an email
          success = true;
          message = 'Resend API key is configured';
          break;
        
        case 'google-docai':
          // Test Document AI by calling extract-text-docai with empty params
          const { error: docaiError } = await supabase.functions.invoke('extract-text-docai', {
            body: { test: true }
          });
          success = !docaiError || docaiError.message?.includes('No file');
          message = success ? 'Document AI connection successful' : docaiError?.message || 'Connection failed';
          break;
        
        default:
          success = true;
          message = 'Integration appears to be configured';
      }

      toast({
        title: success ? 'Test Successful' : 'Test Failed',
        description: message,
        variant: success ? 'default' : 'destructive',
      });
    } catch (error: any) {
      toast({
        title: 'Test Failed',
        description: error.message,
        variant: 'destructive',
      });
    } finally {
      setTestingId(null);
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    toast({
      title: 'Copied',
      description: 'Copied to clipboard',
    });
  };

  const maskSecret = (key: string) => {
    return `${key.substring(0, 4)}${'•'.repeat(20)}`;
  };

  const getStatusBadge = (status: 'connected' | 'partial' | 'not_configured') => {
    switch (status) {
      case 'connected':
        return (
          <Badge className="bg-emerald-500/10 text-emerald-600 border-emerald-500/20">
            <CheckCircle2 className="h-3 w-3 mr-1" />
            Connected
          </Badge>
        );
      case 'partial':
        return (
          <Badge className="bg-amber-500/10 text-amber-600 border-amber-500/20">
            <AlertTriangle className="h-3 w-3 mr-1" />
            Partial
          </Badge>
        );
      case 'not_configured':
        return (
          <Badge variant="outline" className="text-muted-foreground">
            <XCircle className="h-3 w-3 mr-1" />
            Not Configured
          </Badge>
        );
    }
  };

  const getIntegrationsByCategory = (category: string) => {
    return integrations.filter(i => i.category === category);
  };

  const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Integrations</h1>
          <p className="text-muted-foreground mt-1">
            Manage external service connections and API configurations
          </p>
        </div>
        <Button variant="outline" onClick={() => refetch()}>
          <RefreshCw className="h-4 w-4 mr-2" />
          Refresh Status
        </Button>
      </div>

      {/* Overview Cards */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        {(['communication', 'ai', 'document', 'monitoring', 'enrichment'] as const).map(category => {
          const categoryIntegrations = getIntegrationsByCategory(category);
          const connectedCount = categoryIntegrations.filter(
            i => getIntegrationStatus(i) === 'connected'
          ).length;
          const CategoryIcon = categoryIcons[category];

          return (
            <Card key={category}>
              <CardContent className="pt-6">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-primary/10">
                    <CategoryIcon className="h-5 w-5 text-primary" />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">{categoryLabels[category]}</p>
                    <p className="text-2xl font-bold">
                      {connectedCount}/{categoryIntegrations.length}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Integration Tabs */}
      <Tabs defaultValue="all" className="space-y-4">
        <TabsList>
          <TabsTrigger value="all">All Integrations</TabsTrigger>
          <TabsTrigger value="communication">Communication</TabsTrigger>
          <TabsTrigger value="ai">AI Services</TabsTrigger>
          <TabsTrigger value="document">Document Processing</TabsTrigger>
          <TabsTrigger value="monitoring">Monitoring</TabsTrigger>
          <TabsTrigger value="enrichment">Enrichment</TabsTrigger>
        </TabsList>

        {['all', 'communication', 'ai', 'document', 'monitoring', 'enrichment'].map(tab => (
          <TabsContent key={tab} value={tab} className="space-y-4">
            {(tab === 'all' ? integrations : getIntegrationsByCategory(tab)).map(integration => {
              const status = getIntegrationStatus(integration);
              const Icon = integration.icon;
              const hasErrors = recentErrors?.some(
                e => e.error_code?.toLowerCase().includes(integration.id)
              );

              return (
                <Card key={integration.id} className={cn(
                  "transition-all",
                  hasErrors && "border-destructive/50"
                )}>
                  <CardHeader>
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-3">
                        <div className={cn(
                          "p-3 rounded-lg",
                          status === 'connected' ? "bg-emerald-500/10" : "bg-muted"
                        )}>
                          <Icon className={cn(
                            "h-6 w-6",
                            status === 'connected' ? "text-emerald-600" : "text-muted-foreground"
                          )} />
                        </div>
                        <div>
                          <CardTitle className="flex items-center gap-2">
                            {integration.name}
                            {getStatusBadge(status)}
                            {hasErrors && (
                              <Badge variant="destructive" className="text-xs">
                                Recent Errors
                              </Badge>
                            )}
                          </CardTitle>
                          <CardDescription>{integration.description}</CardDescription>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {integration.docsUrl && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => window.open(integration.docsUrl, '_blank')}
                          >
                            <ExternalLink className="h-4 w-4 mr-1" />
                            Docs
                          </Button>
                        )}
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => testIntegration(integration)}
                          disabled={testingId === integration.id || status === 'not_configured'}
                        >
                          {testingId === integration.id ? (
                            <RefreshCw className="h-4 w-4 mr-1 animate-spin" />
                          ) : (
                            <Settings2 className="h-4 w-4 mr-1" />
                          )}
                          Test Connection
                        </Button>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {/* Features */}
                    <div>
                      <Label className="text-xs text-muted-foreground">Features</Label>
                      <div className="flex flex-wrap gap-1 mt-1">
                        {integration.features.map(feature => (
                          <Badge key={feature} variant="secondary" className="text-xs">
                            {feature}
                          </Badge>
                        ))}
                      </div>
                    </div>

                    {/* Secret Keys */}
                    <div className="space-y-2">
                      <Label className="text-xs text-muted-foreground">Required Secrets</Label>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                        {integration.secretKeys.map(key => (
                          <div key={key} className="flex items-center gap-2 p-2 rounded-md bg-muted/50">
                            <code className="text-xs flex-1 truncate">
                              {showSecrets[key] ? key : maskSecret(key)}
                            </code>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-6 w-6"
                              onClick={() => setShowSecrets(prev => ({ ...prev, [key]: !prev[key] }))}
                            >
                              {showSecrets[key] ? (
                                <EyeOff className="h-3 w-3" />
                              ) : (
                                <Eye className="h-3 w-3" />
                              )}
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-6 w-6"
                              onClick={() => copyToClipboard(key)}
                            >
                              <Copy className="h-3 w-3" />
                            </Button>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Webhook URL */}
                    {integration.webhookUrl && supabaseUrl && (
                      <div className="space-y-2">
                        <Label className="text-xs text-muted-foreground">Webhook URL</Label>
                        <div className="flex items-center gap-2 p-2 rounded-md bg-muted/50">
                          <code className="text-xs flex-1 truncate">
                            {supabaseUrl}{integration.webhookUrl}
                          </code>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-6 w-6"
                            onClick={() => copyToClipboard(`${supabaseUrl}${integration.webhookUrl}`)}
                          >
                            <Copy className="h-3 w-3" />
                          </Button>
                        </div>
                      </div>
                    )}

                    {/* Recent Errors */}
                    {hasErrors && (
                      <div className="space-y-2">
                        <Label className="text-xs text-destructive">Recent Errors</Label>
                        <div className="space-y-1">
                          {recentErrors
                            ?.filter(e => e.error_code?.toLowerCase().includes(integration.id))
                            .slice(0, 3)
                            .map(error => (
                              <div 
                                key={error.id} 
                                className="p-2 rounded-md bg-destructive/10 text-xs text-destructive"
                              >
                                <span className="font-mono">{error.error_code}</span>: {error.error_message}
                              </div>
                            ))}
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </TabsContent>
        ))}
      </Tabs>

      {/* Configuration Note */}
      <Card className="border-dashed">
        <CardContent className="pt-6">
          <div className="flex items-start gap-3">
            <div className="p-2 rounded-lg bg-primary/10">
              <Settings2 className="h-5 w-5 text-primary" />
            </div>
            <div>
              <h3 className="font-semibold">Configuration</h3>
              <p className="text-sm text-muted-foreground mt-1">
                Integration secrets are managed through Lovable Cloud. To add or update API keys, 
                use the secrets management in your Lovable project settings or contact your administrator.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
